import { concatLatestFrom } from '@ngrx/operators';
import { Injectable, inject } from '@angular/core';
import * as PatientActions from '../state/patient.actions';
import * as DynamicFormPagerActions from 'Shared/app/dynamic-form/state/actions/dynamic-form-pager.actions';
import * as DynamicFormActions from 'Shared/app/dynamic-form/state/actions/render-dynamic-form.actions';
import { Router } from '@angular/router';
import { EMPTY, of } from 'rxjs';
import { Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { selectCurrentLanguageCode, selectSendBackToInera } from './patient.selectors';
import { AuthenticationService } from '../services/authentication.service';
import { BookingRequestService } from '../services/bookingrequest.service';
import { FormRoutingService } from '../services/form-routing.service';
import { PatientLanguageService } from '../services/patient-language.service';
import * as LanguageFunctions from 'Shared/app/core/helpers/language.helper';
import { WINDOW } from 'Shared/app/core/services/window.service';
import { WFP_ROUTES } from 'projects/patient/src/app/constants/routes';

@Injectable({
  providedIn: 'root'
})
export class PatientEffects {
  readonly #actions$ = inject(Actions);
  readonly #router = inject(Router);
  readonly #store = inject(Store);
  readonly #authenticationService = inject(AuthenticationService);
  readonly #bookingRequestService = inject(BookingRequestService);
  readonly #formRoutingService = inject(FormRoutingService);
  readonly #patientLanguageService = inject(PatientLanguageService);
  readonly #WINDOW = inject<Window>(WINDOW);

  closeDynamicForm$ = createEffect(
    () => {
      return this.#actions$.pipe(
        ofType(
          DynamicFormPagerActions.closeEndPage,
          DynamicFormActions.saveDraftFailure,
          DynamicFormActions.saveResultFailure
        ),
        tap(() => {
          this.#router.navigateByUrl(WFP_ROUTES.SESSION);
        })
      );
    },
    { dispatch: false }
  );

  dynamicFormLoadQuestionnaireFailure$ = createEffect(
    () => {
      return this.#actions$.pipe(
        ofType(DynamicFormActions.loadQuestionnaireFailure),
        tap(() => {
          this.#router.navigateByUrl(WFP_ROUTES.SESSION);
        })
      );
    },
    { dispatch: false }
  );

  dynamicFormDraft$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(DynamicFormActions.saveDraftComplete),
      map(() => {
        this.#router.navigateByUrl('/session');
        return PatientActions.setActiveFormDraft();
      })
    );
  });

  dynamicFormSave$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(DynamicFormActions.saveResultsComplete),
      map((action) => {
        if (!action.result.hasEndPage) {
          this.#router.navigateByUrl(WFP_ROUTES.SESSION);
        }
        return PatientActions.setActiveFormCompleted();
      })
    );
  });

  login$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(PatientActions.login),
      switchMap((action) =>
        this.#authenticationService.login(action.credentials).pipe(
          map((session) => {
            return PatientActions.loginSuccess({ session: session });
          }),
          catchError(() => {
            return of(PatientActions.loginFailure());
          })
        )
      )
    );
  });

  loginSSO$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(PatientActions.loginSSO),
      switchMap(() =>
        this.#authenticationService.loadSessionFromServer().pipe(
          map((session) => {
            return PatientActions.loginSuccess({ session: session });
          }),
          catchError(() => {
            return of(PatientActions.loginFailure());
          })
        )
      )
    );
  });

  loadSessionFromServer$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(PatientActions.loadSessionFromServer),
      switchMap(() =>
        this.#authenticationService.loadSessionFromServer().pipe(
          map((session) => {
            const exceptedRoutes = [WFP_ROUTES.STATEMENT, WFP_ROUTES.LOGOUT, `${WFP_ROUTES.LOGIN}?loginType=sso`];
            if (!exceptedRoutes.includes(this.#router.url)) {
              this.#router.navigateByUrl(WFP_ROUTES.SESSION);
            }
            return PatientActions.loadSessionSuccess({ session: session });
          }),
          catchError(() => {
            if (this.#router.url.split('?')[0] !== WFP_ROUTES.STATEMENT) {
              this.#router.navigateByUrl(WFP_ROUTES.LOGIN);
            }
            return of(PatientActions.loadSessionFailure());
          })
        )
      )
    );
  });

  logout$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(PatientActions.logout),
      switchMap(() =>
        this.#authenticationService.logout().pipe(
          map(() => {
            this.#router.navigateByUrl(WFP_ROUTES.LOGIN);
            return PatientActions.logoutSuccess();
          }),
          catchError(() => {
            this.#router.navigateByUrl(WFP_ROUTES.LOGIN);
            return of(PatientActions.logoutFailure());
          })
        )
      )
    );
  });

  logoutSso$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(PatientActions.logoutSso),
      switchMap(() =>
        this.#authenticationService.logoutSSO().pipe(
          map((signOutData) => {
            this.#WINDOW.location.href = signOutData.url;
            return PatientActions.logoutSuccess();
          }),
          catchError(() => {
            this.#router.navigateByUrl(WFP_ROUTES.LOGIN);
            return of(PatientActions.logoutFailure());
          })
        )
      )
    );
  });

  checkReturnTo1177$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(PatientActions.checkReturnTo1177),
      concatLatestFrom(() => this.#store.select(selectSendBackToInera)),
      switchMap(([, data]) => {
        if (data.shouldReturn) {
          return of(PatientActions.returnToExternalSite({ url: data.returnUrl }));
        } else {
          return EMPTY;
        }
      })
    );
  });

  returnToExternalSite$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(PatientActions.returnToExternalSite),
      switchMap((action) =>
        this.#authenticationService.logout().pipe(
          map(() => {
            setTimeout(() => {
              this.#WINDOW.location.href = action.url;
            }, 50);
            return PatientActions.logoutSuccess();
          }),
          catchError(() => {
            setTimeout(() => {
              this.#WINDOW.location.href = action.url;
            }, 50);
            return of(PatientActions.logoutFailure());
          })
        )
      )
    );
  });

  setAntiforgery$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(
        PatientActions.setAntiforgery,
        PatientActions.loginSuccess,
        PatientActions.logoutSuccess,
        PatientActions.logoutFailure
      ),
      map(() => PatientActions.antiForgerySet())
    );
  });

  // The language chosen on the login page is saved to the new session, the forms are performed in the session's language
  setSessionLanguageOnLoginSuccess$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(PatientActions.loginSuccess),
      map(() => PatientActions.setSessionLanguage())
    );
  });

  loadBookingRequestOnLogin$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(PatientActions.loginSuccess),
      switchMap((action) =>
        this.#bookingRequestService.getBookingRequests(action.session.bookingRequestId).pipe(
          map((bookingRequest) => {
            if (
              bookingRequest.forms?.length === 1 &&
              bookingRequest.forms[0].formType === 4 &&
              bookingRequest.forms[0].status === 10
            ) {
              const form = bookingRequest.forms[0];
              this.#formRoutingService.performForm(form.renderType, form.formId, form.id);
            } else {
              this.#router.navigateByUrl(WFP_ROUTES.SESSION);
            }
            return PatientActions.loadBookingRequestSuccess({ bookingRequest: bookingRequest });
          }),
          catchError(() => {
            return of(PatientActions.loadBookingRequestFailure());
          })
        )
      )
    );
  });

  loadBookingRequestOnReload$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(PatientActions.loadSessionSuccess),
      switchMap((action) =>
        this.#bookingRequestService.getBookingRequests(action.session.bookingRequestId).pipe(
          map((bookingRequest) => {
            return PatientActions.loadBookingRequestSuccess({ bookingRequest: bookingRequest });
          }),
          catchError(() => {
            return of(PatientActions.loadBookingRequestFailure());
          })
        )
      )
    );
  });

  setSessionLanguageOnLoadSessionSuccess$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(PatientActions.loadSessionSuccess),
      map((action) => {
        if (action.session.languageId) {
          const language = LanguageFunctions.getLanguageObject(action.session.languageId);
          return PatientActions.setCurrentLanguage({ language: language.code, hasSession: true });
        } else {
          return PatientActions.keepCurrentLanguage();
        }
      })
    );
  });

  loadBookingRequest$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(PatientActions.loadBookingRequest),
      switchMap((action) =>
        this.#bookingRequestService.getBookingRequests(action.bookingRequestId).pipe(
          map((bookingRequest) => {
            return PatientActions.loadBookingRequestSuccess({ bookingRequest: bookingRequest });
          }),
          catchError(() => {
            return of(PatientActions.loadBookingRequestFailure());
          })
        )
      )
    );
  });

  loadSupportedLanguages$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(PatientActions.loadSupportedLanguagesFromServer),
      switchMap(() =>
        this.#patientLanguageService.getSupportedLanguages().pipe(
          map((languages) => {
            return PatientActions.loadSupportedLanguagesSuccess({ supportedLanguages: languages });
          }),
          catchError(() => {
            return of(PatientActions.loadSupportedLanguagesFailure());
          })
        )
      )
    );
  });

  setCurrentLanguage$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(PatientActions.setCurrentLanguage),
      switchMap((action) => {
        if (action.hasSession) {
          return of(PatientActions.saveCurrentLanguage({ language: action.language }));
        } else {
          return of(PatientActions.setSessionLanguageFailure());
        }
      })
    );
  });

  saveCurrentLanguage$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(PatientActions.saveCurrentLanguage),
      switchMap((action) =>
        this.#authenticationService.setSessionLanguage(action.language).pipe(
          map((session) => {
            return PatientActions.setSession({ session: session });
          }),
          catchError(() => {
            return of(PatientActions.setSessionLanguageFailure());
          })
        )
      )
    );
  });

  setSessionLanguageOnLogin$ = createEffect(() => {
    return this.#actions$.pipe(
      ofType(PatientActions.setSessionLanguage),
      concatLatestFrom(() => this.#store.select(selectCurrentLanguageCode)),
      switchMap(([, currentLanguage]) => {
        return of(PatientActions.setCurrentLanguage({ language: currentLanguage, hasSession: true }));
      })
    );
  });
}
